'use client';

/**
 * VerificationBadge — compact deliverability verdict for an enriched email.
 *
 * Shared by the inbox context pane and the audience picker. Quiet Lantern
 * surfaces; the provider (e.g. "found via ContactOut") rides in the tooltip.
 */

import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import type { EmailVerificationStatus } from '../types';

const META: Record<
    EmailVerificationStatus,
    { label: string; icon: typeof CheckCircle2; color: string; bg: string }
> = {
    deliverable: { label: 'Verified', icon: CheckCircle2, color: 'var(--fg-success)', bg: 'var(--bg-success)' },
    risky: { label: 'Risky', icon: AlertTriangle, color: 'var(--fg-warning)', bg: 'var(--bg-warning)' },
    undeliverable: { label: 'Undeliverable', icon: XCircle, color: 'var(--fg-danger)', bg: 'var(--bg-danger)' },
    unknown: { label: 'Unverified', icon: HelpCircle, color: 'var(--text-tertiary)', bg: 'var(--surface-tertiary)' },
};

export function VerificationBadge({
    status,
    provider,
}: {
    status: EmailVerificationStatus;
    /** Finder that produced the email — shown in the tooltip. */
    provider?: string | null;
}) {
    const m = META[status] || META.unknown;
    const Icon = m.icon;
    const title = provider ? `${m.label} · found via ${provider}` : m.label;
    return (
        <span
            title={title}
            className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
            style={{ color: m.color, background: m.bg }}
        >
            <Icon size={11} strokeWidth={2} />
            {m.label}
        </span>
    );
}
